import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs/operators';

import { AuthenticationDataService } from '../authentication';
import { UserRole } from '../authentication/enums/user-role.enum';
import { Logger } from '../logger/services/logger.service';

import { IAccountChat } from './account-chat';
import { ChatDataService } from './chat-data.service';
import { NgxZendeskWebwidgetService } from './zendesk-webwidget/zendesk-webwidget.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly ngxZendeskWebwidgetService = inject(NgxZendeskWebwidgetService);
  private readonly chatDataService = inject(ChatDataService);
  private readonly authenticationDataService = inject(AuthenticationDataService);
  private readonly logger = inject(Logger);

  private initializing = false;

  public view(): void {
    // Retrieve user claims from localStorage since Zendesk is initialized on-demand
    // (at chat button click) rather than at login time
    const userClaims = this.authenticationDataService.getUserClaims();
    if (!userClaims || !userClaims.roles?.includes(UserRole.teacher)) {
      return;
    }

    if (this.ngxZendeskWebwidgetService.isInitialized) {
      this.ngxZendeskWebwidgetService.zE('webWidget', 'show');
      this.ngxZendeskWebwidgetService.zE('webWidget', 'open');
      return;
    }

    if (this.initializing) {
      return;
    }

    this.initializing = true;
    this.ngxZendeskWebwidgetService.initZendesk().then(() => {
      this.ngxZendeskWebwidgetService.zE('webWidget', 'prefill', {
        name: {
          value: userClaims.userName,
          readOnly: true
        }
      });

      this.chatDataService.getChatInfo()
        .pipe(
          tap((account: IAccountChat) => this.addUserInfo(account)),
          untilDestroyed(this)
        )
        .subscribe();

      this.ngxZendeskWebwidgetService.zE('webWidget', 'show');
      this.ngxZendeskWebwidgetService.zE('webWidget', 'open');
    }).catch((error) => {
      this.logger.error('ZendeskInitFailure', error);
    }).finally(() => {
      this.initializing = false;
    });
  }

  private addUserInfo(account: IAccountChat): void {
    this.ngxZendeskWebwidgetService.zE('webWidget', 'prefill', {
      email: {
        value: account.Email,
        readOnly: true
      },
      phone: {
        value: account.PhoneNumber,
        readOnly: true
      }
    });

    this.ngxZendeskWebwidgetService.zE('webWidget', 'updateSettings', {
      webWidget: {
        chat: {
          tags: ['School: ' + account.SchoolName, 'Board: ' + account.BoardName]
        }
      }
    });
  }

  public logOut(): void {
    if (this.ngxZendeskWebwidgetService.isInitialized) {
      this.ngxZendeskWebwidgetService.zE('webWidget', 'logout');
      this.ngxZendeskWebwidgetService.zE('webWidget', 'hide');
    }
  }
}
